import type { Notification } from "../api-models";

export interface DocTarget {
  /** Path to open. For DOC_ROUTES this is the list page; the detail page is `${path}/${id}`. */
  path: string;
  /** Permission needed to open it — the server re-checks, this only avoids dead links. */
  permission: string;
}

/**
 * Where each document type's detail page lives. Keyed by the server's DocType constants
 * (constants.go), so anything carrying a document type — an approval instance, a
 * notification's entityType, a cross-document reference — resolves through this one table.
 */
export const DOC_ROUTES = {
  BOM_VERSION: { path: "/bom-versions", permission: "BOM.Read" },
  BOM_VARIATION: { path: "/bom-variations", permission: "BOM.Read" },
  MRQ: { path: "/requests", permission: "Requests.Read" },
  GIN: { path: "/issues", permission: "GIN.Read" },
  BOAT_NOTE: { path: "/boat-notes", permission: "BoatNotes.Read" },
  MRN: { path: "/receipts", permission: "MRN.Read" },
  CONSUMPTION: { path: "/consumption", permission: "Consumption.Read" },
  TOOL_CUSTODY: { path: "/tools", permission: "Tools.Read" },
  TRANSFER_ORDER: { path: "/transfers", permission: "Transfers.Read" },
  SITE_RETURN: { path: "/returns", permission: "Returns.Read" },
  SUPPLIER_RETURN: { path: "/supplier-returns", permission: "SupplierReturns.Read" },
  PR: { path: "/procurement/prs", permission: "PR.Read" },
  RFQ: { path: "/procurement/rfqs", permission: "Quotations.Read" },
  PO: { path: "/procurement/pos", permission: "PO.Read" },
  // An amendment is reached by its own id; the poId-scoped route is only used when drafting.
  PO_AMENDMENT: { path: "/procurement/amendments", permission: "PO.Read" },
  GRN: { path: "/procurement/grns", permission: "GRN.Read" },
  GIN_DIRECT: { path: "/procurement/gin-directs", permission: "GINDirect.Read" },
  DISCREPANCY: { path: "/discrepancies", permission: "Discrepancies.Read" },
  STOCK_ADJUSTMENT_SITE: { path: "/adjustments", permission: "Adjustments.Read" },
  STOCK_ADJUSTMENT_WAREHOUSE: { path: "/adjustments", permission: "Adjustments.Read" },
  ATTENDANCE_SHEET: { path: "/attendance", permission: "Attendance.Read" },
  // Global search emits one kind for all five service-request types rather than
  // reconstructing docType() in SQL; they share a register and a detail route anyway.
  SERVICE_REQUEST: { path: "/service-requests", permission: "ServiceRequests.Read" },
  SR_VEHICLE: { path: "/service-requests", permission: "ServiceRequests.Read" },
  SR_STAFF_TRANSFER: { path: "/service-requests", permission: "ServiceRequests.Read" },
  SR_EQUIPMENT_TRANSFER: { path: "/service-requests", permission: "ServiceRequests.Read" },
  SR_TRANSPORT: { path: "/service-requests", permission: "ServiceRequests.Read" },
  SR_RENTAL: { path: "/service-requests", permission: "ServiceRequests.Read" },
} as const satisfies Record<string, DocTarget>;

export type DocType = keyof typeof DOC_ROUTES;

/** The detail page for a document, or null for a type with no page of its own. */
export function docTarget(docType: string, id: number): DocTarget | null {
  const route: DocTarget | undefined = DOC_ROUTES[docType as DocType];
  return route ? { path: `${route.path}/${id}`, permission: route.permission } : null;
}

// The two item alerts point at an Item, which has no detail page — they open the report
// that explains why the alert was raised instead.
const ITEM_ALERT_TARGETS: Partial<Record<Notification["type"], DocTarget>> = {
  LOW_STOCK: { path: "/reports/low-stock", permission: "Reports.Read" },
  OVER_CONSUMPTION: { path: "/reports/bom-vs-actual", permission: "Reports.Read" },
};

/**
 * Where a notification takes the user. Approval notifications carry the referring
 * document's DocType in `entityType`; item alerts fall back to their report.
 */
export function notificationTarget(n: Notification): DocTarget | null {
  return docTarget(n.entityType, n.entityId) ?? ITEM_ALERT_TARGETS[n.type] ?? null;
}
